import React, { FC, useState } from 'react'
import { Trans } from '@lingui/macro'

import pathwayClone from '../../operations/pathwayClone'
import { Page } from '../../components/common/page/Page'

import { ItemList } from '../../components/common/list/ItemList'
import { IconPlus } from '../../components/common/icon/IconPlus'
import { ButtonPrimary } from 'front-end/src/components'
import PathwaysPageFilters, {
  pathwayStateSelectOptions,
  PathwaysPageFilterProps,
  PathwayOrderOptions,
  pathwayOrderingOptions
} from './components/PathwaysPageFilters'

import { PathwayItem } from './components/PathwayItem'
import PathwayCreateModal from './components/PathwayCreateModal'
import { IPathway } from './types'
import { linkBuilder } from 'shared/linkBuilder'
import { navigateTo } from '../../app/util/appHistory'

import './ManagePathwaysPage.less'
import { useManagePathwaysPageQuery } from './ManagePathwaysPage.codegen'
import {
  PathwayModelAsAdminFilterEnum,
  PathwayModelAsAdminFilterInput
} from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { ManagePathwaysTopbar } from './components/ManagePathwaysTopbar'
import { DEFAULT_PAGE_LIMIT } from 'front-end/src/components/common/table-extended/TableExtended'

export const ManagePathwaysPage: FC = () => {
  const [state, setState] = useState({
    filter: {
      search: '',
      published: pathwayStateSelectOptions[0] as PathwaysPageFilterProps,
      sort: pathwayOrderingOptions[0]
    },
    showCreateModal: false,
    loading: false,
    pending: true,
    page: 1,
    pageSize: DEFAULT_PAGE_LIMIT
  })

  const {
    pageSize,
    page,
    filter: { search, published, sort }
  } = state
  const filters: Array<PathwayModelAsAdminFilterInput> = []

  if (search) {
    filters.push({
      name: PathwayModelAsAdminFilterEnum.title,
      value: search
    })
  }
  if (published.value !== null) {
    filters.push({
      name: PathwayModelAsAdminFilterEnum.published,
      value: published.value
    })
  }

  const { data, refetch } = useManagePathwaysPageQuery({
    variables: {
      filters: filters,
      limit: pageSize,
      order: {
        name: sort.value.name,
        sort: sort.value.sort
      },
      offset: (page - 1) * pageSize
    }
  })

  const handlePathwayCreated = (pathway: { id: number }): void => {
    navigateTo(linkBuilder.pathwayDetailManage(pathway.id))
  }

  const handleCreateModalVisibility = (showCreateModal: boolean): void => {
    setState({ ...state, showCreateModal })
  }

  const handlePageChange = (page: number): void => {
    setState({ ...state, page })
  }

  const handleSearchChange = (
    search: string,
    published: PathwaysPageFilterProps,
    sort: PathwayOrderOptions
  ): void => {
    setState({
      ...state,
      page: 1,
      filter: {
        search,
        published,
        sort
      }
    })
  }

  const handleClone = async (id: number): Promise<void> => {
    const pathway = await pathwayClone(id)
    navigateTo(linkBuilder.pathwayDetailManage(pathway.id))
  }

  const renderTableHeading = () => {
    const { filter } = state

    return (
      <>
        <PathwaysPageFilters
          search={filter.search}
          state={filter.published}
          sort={filter.sort}
          onSearchChange={handleSearchChange}
        />
        <ButtonPrimary
          onClick={() => handleCreateModalVisibility(true)}
          leftIcon={<IconPlus />}
        >
          <Trans>Create a pathway</Trans>
        </ButtonPrimary>
      </>
    )
  }

  const { loading, showCreateModal } = state
  const pathwaysCount = data?.admin?.organisation?.pathwaysCount ?? 0
  const isPageLast = Math.ceil(pathwaysCount / pageSize) === page

  const pathways = data?.admin?.organisation?.pathways
  return (
    <>
      <ManagePathwaysTopbar />
      <Page withTopbar className="ManagePathwaysPage">
        <ItemList
          className="Pathways__list"
          page={page}
          items={pathways ?? []}
          itemsCount={pathwaysCount}
          loading={loading}
          pageSize={pageSize}
          renderItem={(pathway: IPathway, idx: number | undefined) => (
            <PathwayItem
              canReorder={sort.value.name === 'order'}
              onChange={refetch}
              first={page === 1 && idx === 0}
              last={isPageLast && idx === pathwaysCount - 1}
              pathway={pathway}
              onClone={() => handleClone(pathway.id)}
            />
          )}
          header={renderTableHeading()}
          onPageChange={handlePageChange}
        />

        {showCreateModal && (
          <PathwayCreateModal
            onCancel={() => {
              handleCreateModalVisibility(false)
            }}
            onPathwayCreated={handlePathwayCreated}
          />
        )}
      </Page>
    </>
  )
}
